import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Select,
  FormControl,
  FormLabel,
  useToast,
  Box,
  useColorMode,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../config/googleMapsConfig';
import { useSublocations } from '../../hooks/useSublocations';
import { validateCoordinates, normalizeCoordinates } from '../../utils/locationUtils';

// Dark mode style for archaeological mapping
const mapStyles = {
  light: [], // Default Google Maps style
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#2D2A26' }] }, // ancient.stone dark variant
    { elementType: 'labels.text.stroke', stylers: [{ color: '#2D2A26' }] }, // ancient.stone dark variant
    { elementType: 'labels.text.fill', stylers: [{ color: '#8A7266' }] }, // ancient.terracotta
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#A89A78' }], // ancient.gold
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{ color: '#3D332E' }], // darker terracotta
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#A89A78' }], // ancient.gold
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#4A4540' }], // darker ancient.stone
    },
  ],
};

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

export default function SubLocationMapModal({
  isOpen,
  onClose,
  siteId,
  mediaId,
  currentSubLocation,
  onSubLocationSelect,
  siteLocation // Site location prop
}) {
  const toast = useToast();
  const { colorMode } = useColorMode();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || ''
  });

  // Add error handling for map loading
  useEffect(() => {
    if (loadError) {
      console.error('Error loading maps:', loadError);
      toast({
        title: "Map Loading Error",
        description: "There was an error loading Google Maps. Please check your API key configuration.",
        status: "error",
        duration: 5000,
        isClosable: true,
        variant: "subtle",
        containerStyle: {
          borderWidth: '1px',
          borderColor: 'red.500',
          bg: useColorModeValue('white', 'gray.800')
        }
      });
    }
  }, [loadError, toast]);
  
  const {
    sublocations,
    loading: sublocationsLoading,
    error: sublocationsError,
    createSublocation,
  } = useSublocations(siteId);
  
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);
  const [newLocationName, setNewLocationName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  // Default to San Francisco if no valid site location
  const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };
  
  const getInitialMapCenter = () => {
    console.log('Current sublocation:', currentSubLocation);
    console.log('Site location:', siteLocation);
    
    // Try current sublocation first
    if (currentSubLocation?.coordinates) {
      const validatedCoords = validateCoordinates(currentSubLocation.coordinates);
      if (validatedCoords) {
        console.log('Using current sublocation coordinates:', validatedCoords);
        return validatedCoords;
      }
    }
    
    // Then try site location
    if (siteLocation) {
      const validatedCoords = validateCoordinates(siteLocation);
      if (validatedCoords) {
        console.log('Using site coordinates:', validatedCoords);
        return validatedCoords;
      }
    }
    
    console.log('Using default center:', DEFAULT_CENTER);
    return DEFAULT_CENTER;
  };

  const [mapCenter, setMapCenter] = useState(getInitialMapCenter());

  // Initialize state when modal opens or when sublocation/site changes
  useEffect(() => {
    if (isOpen) {
      if (currentSubLocation) {
        setSelectedSubLocation(currentSubLocation.id);
        
        if (currentSubLocation.coordinates) {
          const validatedCoords = validateCoordinates(currentSubLocation.coordinates);
          if (validatedCoords) {
            setMarkerPosition(validatedCoords);
            setMapCenter(validatedCoords);
            return;
          }
        }
      }
      
      // If we don't have valid sublocation coordinates, try to use site location
      if (siteLocation) {
        const validatedCoords = validateCoordinates(siteLocation);
        if (validatedCoords) {
          setMapCenter(validatedCoords);
          setMarkerPosition(validatedCoords); // Set marker to site location as fallback
          return;
        }
      }
      
      // If we have neither, use default center
      setMapCenter(DEFAULT_CENTER);
      setMarkerPosition(DEFAULT_CENTER);
    }
  }, [isOpen, currentSubLocation, siteLocation]);

  const handleMapClick = (event) => {
    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    const validatedPosition = validateCoordinates(newPosition);
    if (validatedPosition) {
      setMarkerPosition(validatedPosition);
    } else {
      toast({
        title: "Invalid Coordinates",
        description: "The selected location has invalid coordinates",
        status: "error",
        duration: 3000,
        variant: "subtle",
        containerStyle: {
          borderWidth: '1px',
          borderColor: 'red.500',
          bg: useColorModeValue('white', 'gray.800')
        }
      });
    }
  };

  const handleSubLocationChange = (subLocationId) => {
    const subLocation = sublocations.find(sl => sl.id === subLocationId);
    setSelectedSubLocation(subLocationId);
    
    if (subLocation?.coordinates) {
      const validatedCoords = validateCoordinates(subLocation.coordinates);
      if (validatedCoords) {
        setMarkerPosition(validatedCoords);
        setMapCenter(validatedCoords);
      } else {
        // If coordinates are invalid, fall back to site location
        const siteFallback = validateCoordinates(siteLocation);
        if (siteFallback) {
          setMarkerPosition(siteFallback);
          setMapCenter(siteFallback);
        }
        toast({
          title: "Warning",
          description: "Invalid coordinates for selected location, using site location instead",
          status: "warning",
          duration: 3000,
          variant: "subtle",
          containerStyle: {
            borderWidth: '1px',
            borderColor: 'orange.500',
            bg: useColorModeValue('white', 'gray.800')
          }
        });
      }
    }
  };

  const handleSave = async () => {
    if (!selectedSubLocation) {
      toast({
        title: "Missing Information",
        description: "Please select a location from the dropdown or create a new one",
        status: "error",
        duration: 3000,
        variant: "subtle",
        containerStyle: {
          borderWidth: '1px',
          borderColor: 'red.500',
          bg: useColorModeValue('white', 'gray.800')
        }
      });
      return;
    }

    const validatedCoords = validateCoordinates(markerPosition);
    if (!validatedCoords) {
      toast({
        title: "Invalid Location",
        description: "Please place a valid marker on the map by clicking a location",
        status: "error",
        duration: 3000,
        variant: "subtle",
        containerStyle: {
          borderWidth: '1px',
          borderColor: 'red.500',
          bg: useColorModeValue('white', 'gray.800')
        }
      });
      return;
    }

    try {
      // Ensure coordinates are properly structured in consistent format
      await onSubLocationSelect({
        id: selectedSubLocation,
        coordinates: {
          lat: Number(validatedCoords.lat),
          lng: Number(validatedCoords.lng)
        }
      });
      
      console.log('[SubLocationMapModal] Saved sublocation with coordinates:', validatedCoords);
      toast({
        title: "Success",
        description: "Location has been saved successfully",
        status: "success",
        duration: 3000,
        variant: "subtle",
        containerStyle: {
          borderWidth: '1px',
          borderColor: 'ancient.terracotta',
          bg: useColorModeValue('white', 'gray.800')
        }
      });
      onClose();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        variant: "subtle",
        containerStyle: {
          borderWidth: '1px',
          borderColor: 'red.500',
          bg: useColorModeValue('white', 'gray.800')
        }
      });
    }
  };

  if (sublocationsLoading) {
    return <Text>Loading locations...</Text>;
  }

  if (sublocationsError) {
    return <Text color="ancient.terracotta">Error loading locations: {sublocationsError}</Text>;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader>Tag Image Location</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} width="100%">
            <FormControl width="100%" isRequired>
              <FormLabel>Select Location</FormLabel>
              {isCreating ? (
                <InputGroup>
                  <Input
                    value={newLocationName}
                    onChange={(e) => setNewLocationName(e.target.value)}
                    placeholder="Enter new location name"
                  />
                  <InputRightElement>
                    <IconButton
                      icon={<AddIcon />}
                      size="sm"
                      bg="ancient.terracotta"
                      color="white"
                      _hover={{ bg: 'ancient.stone' }}
                      onClick={async () => {
                        if (!newLocationName.trim()) {
                          toast({
                            title: "Required",
                            description: "Please enter a location name",
                            status: "error",
                            duration: 3000,
                            variant: "subtle",
                            containerStyle: {
                              borderWidth: '1px',
                              borderColor: 'red.500',
                              bg: useColorModeValue('white', 'gray.800')
                            }
                          });
                          return;
                        }
                        try {
                          const validatedCoords = validateCoordinates(markerPosition);
                          if (!validatedCoords) {
                            toast({
                            title: "Invalid Coordinates",
                            description: "Please select a valid location on the map",
                            status: "error",
                            duration: 3000,
                            variant: "subtle",
                            containerStyle: {
                              borderWidth: '1px',
                              borderColor: 'red.500',
                              bg: useColorModeValue('white', 'gray.800')
                            }
                            });
                            return;
                          }

                          // Ensure coordinates are properly structured as a nested object
                          const id = await createSublocation({
                            name: newLocationName.trim(),
                            coordinates: {
                              lat: Number(validatedCoords.lat),
                              lng: Number(validatedCoords.lng)
                            }
                          });
                          
                          console.log('[SubLocationMapModal] Created new sublocation with coordinates:', validatedCoords);
                          setNewLocationName('');
                          setIsCreating(false);
                          setSelectedSubLocation(id);
                          toast({
                            title: "Success",
                            description: "Location created successfully",
                            status: "success",
                            duration: 3000,
                            variant: "subtle",
                            containerStyle: {
                              borderWidth: '1px',
                              borderColor: 'ancient.terracotta',
                              bg: useColorModeValue('white', 'gray.800')
                            }
                          });
                        } catch (err) {
                          toast({
                            title: "Error",
                            description: err.message,
                            status: "error",
                            duration: 3000,
                            variant: "subtle",
                            containerStyle: {
                              borderWidth: '1px',
                              borderColor: 'red.500',
                              bg: useColorModeValue('white', 'gray.800')
                            }
                          });
                        }
                      }}
                      aria-label="Create location"
                    />
                  </InputRightElement>
                </InputGroup>
              ) : (
                <VStack spacing={2} width="100%">
                  <Select
                    value={selectedSubLocation || ''}
                    onChange={(e) => handleSubLocationChange(e.target.value)}
                    placeholder="Choose a location"
                  >
                    {sublocations.map((sl) => (
                      <option key={sl.id} value={sl.id}>
                        {sl.name}
                      </option>
                    ))}
                  </Select>
                  <Button
                    leftIcon={<AddIcon />}
                    size="sm"
                    variant="outline"
                    color="ancient.stone"
                    borderColor="ancient.stone"
                    _hover={{ bg: 'ancient.subtle' }}
                    onClick={() => setIsCreating(true)}
                    width="100%"
                  >
                    Create New Location
                  </Button>
                </VStack>
              )}
            </FormControl>

            <Box width="100%" height="400px" borderWidth="1px" borderRadius="md">
              {!isLoaded ? (
                <Box h="400px" display="flex" alignItems="center" justifyContent="center" bg="ancient.subtle">
                  <Text>Loading map...</Text>
                </Box>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={18}
                  onClick={handleMapClick}
                  options={{
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                    mapTypeId: 'hybrid',
                    styles: colorMode === 'dark' ? mapStyles.dark : mapStyles.light
                  }}
                >
                  {/* Main site marker */}
                  <MarkerF
                    position={mapCenter}
                    icon={{
                      url: '/marker-main.svg',
                      scaledSize: isLoaded ? new window.google.maps.Size(40, 40) : null
                    }}
                  />

                  {/* Selected sublocation marker */}
                  {markerPosition && (
                    <MarkerF
                      position={markerPosition}
                      draggable={true}
                      icon={{
                        url: '/marker-sub.svg',
                        scaledSize: isLoaded ? new window.google.maps.Size(30, 30) : null
                      }}
                      onDragEnd={(e) => {
                        const newPosition = {
                          lat: e.latLng.lat(),
                          lng: e.latLng.lng(),
                        };
                        const validatedPosition = validateCoordinates(newPosition);
                        if (validatedPosition) {
                          setMarkerPosition(validatedPosition);
                        } else {
          toast({
            title: "Invalid Coordinates",
            description: "The marker was moved to an invalid location",
            status: "error",
            duration: 3000,
            variant: "subtle",
            containerStyle: {
              borderWidth: '1px',
              borderColor: 'red.500',
              bg: useColorModeValue('white', 'gray.800')
            }
          });
                        }
                      }}
                    />
                  )}
                </GoogleMap>
              )}
            </Box>

            {markerPosition && (
              <Text fontSize="sm" color="ancient.lightStone">
                Selected coordinates: {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            bg="ancient.terracotta"
            color="white"
            _hover={{ bg: 'ancient.stone' }}
            mr={3} 
            onClick={handleSave}
          >
            Save Location
          </Button>
          <Button 
            variant="outline"
            color="ancient.stone"
            borderColor="ancient.stone"
            _hover={{ bg: 'ancient.subtle' }}
            onClick={onClose}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
